import { useContext, useState, useEffect, useCallback } from 'react';
import { CustomerContext } from './CustomerProvider';

// Service
import CustomerService from '../../services/customerService';

const useCustomer = ( customerId ) => {
    const [customer, reducer] = useContext(CustomerContext);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        pageSize: 10,
        pageNumber: 1,
        totalPage: 1
    })

    const replaceTaxConfig = useCallback(( taxConfig ) => {
        reducer({ type: 'REPLACE_TAXCONFIG', payload: taxConfig });
    }, [reducer])

    useEffect(() => {
        const FindCustomer = () => {
            setLoading(true);
            CustomerService
                .Get( customerId )
                .then( (data) => reducer({ type: 'SET_CUSTOMER', payload: data }))
                .finally(() => setLoading(false) );
        }
        
        if (customerId && customer.id !== customerId) {
            FindCustomer();
        } else setLoading(false);
    }, [customer.id, customerId, reducer])

    const FindAddress = useCallback(() => {
        if (!customer?.id || !pagination?.pageNumber || !pagination?.pageSize) return;
        
        setLoading(true);
        CustomerService
            .GetAddresses(customer.id, {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize
            })
            .then(addresses => {
                reducer({ type: 'REPLACE_ADDRESS', payload: addresses.values })
                setPagination(p => ({...p, totalPage: addresses.totalPage}))
            })
            .finally(() => setLoading(false));
    }, [customer?.id]);

    useEffect(() => {
        if (customer.id && (!customer.addresses?.length || customerId !== customer.id)) {
            FindAddress();
        } else setLoading(false);
    }, [customer.addresses?.length, customer.id, customerId, pagination.pageSize, pagination.pageNumber, reducer])

    useEffect(() => {
        const GetTaxConfig = () => {
            setLoading(true);
            CustomerService
                .GetTaxConfig(customer.id)
                .then(taxConfig => replaceTaxConfig(taxConfig))
                .finally(() => setLoading(false));
        }

        if (customer.id && !customer.taxConfig) {
            GetTaxConfig();
        } else setLoading(false);
    }, [customer.id, customer.taxConfig, replaceTaxConfig])

    const edit = useCallback(( customer ) => {
        reducer({ type: 'EDIT_CUSTOMER', payload: customer });
    }, [reducer])

    const replaceCustomer = ( newCustomer ) => {
        reducer({ type: 'REPLACE_CUSTOMER', payload: newCustomer });
    }

    const addAddress = async (address) => {
        if (customer.id)
            address = await CustomerService.CreateAddress(customerId, address)
        else 
            address.id = address.street.replace(/\s/g, '')
        
        reducer({ type: 'ADD_ADDRESS', payload: address })
        return address
    }

    const updateAddress = async (address) => {
        if (customer.id) {
            address = await CustomerService.UpdateAddress(customer.id, address)
            FindAddress()
        }
        reducer({ type: 'UPDATE_ADDRESS', payload: address })
        return address
    }

    const deleteAddress = async (address) => {
        if (customer.id)
            await CustomerService.DeleteAddress(customerId, address.id)

        reducer({ type: 'DELETE_ADDRESS', payload: address })
    }

    const saveAddress = async (address) => {
        const res = address.id ? await updateAddress(address) : await addAddress(address)
        return res
    }

    const save = async ( newCustomer ) => {
        const customer = newCustomer.id
            ? await CustomerService.Update(newCustomer)
            : await CustomerService.Create(newCustomer);
        
        if (customer.id) {
            const addresses = await CustomerService
                .GetAddresses(customer.id, {
                    pageNumber: pagination.pageNumber,
                    pageSize: pagination.pageSize
                });
            customer.addresses = addresses.values;
        }
        
        replaceCustomer( customer );
        return customer;
    }

    const changePage = (pageNumber) => 
        setPagination({
            ...pagination,
            pageNumber
        })

    return {
        customer,
        loading,
        ...pagination,
        edit,
        save,
        replaceCustomer,
        saveAddress,
        deleteAddress,
        replaceTaxConfig,
        changePage
    };
}

export default useCustomer;
